import { AppRoutes } from 'config';
import { ListCardStyle } from 'types';
import { joinRoutes, formatPrice } from 'utils/helpers';
import { EventReport, EventProfitAndLoss } from 'store/api/event';

const { Profile, Employees } = AppRoutes;

export const revenueTitles = ['Date', 'Check', 'Cash', 'Net Credit', 'Tip'];
export const expensesTitles = ['Category Name', 'Amount'];
export const expensesTemplate = 'repeat(2, 1fr)';

export const commonProps = {
  hasMinHeight: true,
  mainWrapperStyles: { padding: 0 },
};

export const calculatePaymentMethods = (eventReports?: EventReport[]) =>
  eventReports?.reduce(
    (acc, report) => {
      for (const [method, amount] of Object.entries(report?.paymentMethods)) {
        acc[method] += amount;
      }
      return acc;
    },
    { cash: 0, check: 0, credit: 0 } as Record<string, any>
  );

export const MISSING_SALES_REVENUE_NAME = 'Missing sales';

// Missing sales (never rung up in the register) as one labelled Revenue line,
// in the same columns as the register days: sales only (refunds are their own
// P&L line), each amount incl. tax, tip and fee like the register's payments.
// Cards and "other" tenders go to Net Credit. Their tips are in Tip, so the
// Total Revenue row adds up to the rows above it.
export const getMissingSalesRevenue = (data?: EventProfitAndLoss) => {
  if (!data?.missingSalesRowCount) {
    return undefined;
  }
  const byTender = data.missingSalesByTender || {};
  const sum = (tenders: string[]) =>
    Math.round(
      tenders.reduce(
        (acc, tender) => acc + Number(byTender[tender]?.total || 0) * 100,
        0
      )
    ) / 100;
  return {
    cash: sum(['cash']),
    check: sum(['check']),
    tip: Number(data.totalMissingSalesTip || 0),
    credit: sum(['clover_card', 'payanywhere_card', 'other']),
  };
};

export const commonFooterCardProps = {
  isOdd: true,
  selected: true,
  odd: ListCardStyle.Default,
};

export const LTL_EXPENSE_NAME = 'LTL';

// Stock order LTL and stock transfer LTL are one P&L line (Owner request).
export const getCombinedLtlAmount = (data?: EventProfitAndLoss) =>
  data
    ? Number(data.totalLTLStockOrders || 0) +
      Number(data.totalLTLStockTransfers || 0)
    : undefined;

export const TIPS_EXPENSE_NAME = 'Tips';
export const EVENT_MISC_NAME = 'Event Misc';

// Tax is what is owed: collected less the tax given back in refunds.
const getTaxNote = (data?: EventProfitAndLoss) =>
  data?.totalRefundedTax
    ? `Net of ${formatPrice(data.totalRefundedTax)} refunded`
    : undefined;

export const getExpensesInfo = (data?: EventProfitAndLoss) => [
  { name: 'Commissions', amount: data?.totalCommissions },
  // Paid out to the reps (calculated, no records behind it here). Only when
  // the backend sends it, so the Total Expenses sum never meets undefined.
  ...(data?.totalTipsExpense === undefined
    ? []
    : [{ name: TIPS_EXPENSE_NAME, amount: data.totalTipsExpense }]),
  { name: 'Tax', amount: data?.totalTax, note: getTaxNote(data) },
  { name: 'Processing Fee', amount: data?.totalProcessingFees },
  { name: 'Product Cost', amount: data?.totalProductSoldCost },
  { name: LTL_EXPENSE_NAME, amount: getCombinedLtlAmount(data) },
  { name: 'Reimbursements', amount: data?.totalReimbursements },
  { name: 'Bonuses', amount: data?.totalBonuses },
  { name: 'Locations Rent', amount: data?.totalLocationsRent },
  { name: 'Electricity', amount: data?.totalElectricity },
  { name: 'COI', amount: data?.totalCOI },
  // Event payments typed misc (badges, passes, tickets); only when the
  // backend sends it, so the Total Expenses sum never meets undefined.
  ...(data?.totalEventMisc === undefined
    ? []
    : [{ name: EVENT_MISC_NAME, amount: data.totalEventMisc }]),
  { name: 'Shipment Cost', amount: data?.totalShipmentCost },
];

export const getSummaryInfo = (data?: EventProfitAndLoss) => [
  { title: 'Gross Revenue', value: formatPrice(data?.totalGrossSales) },
  { title: 'Net Revenue', value: formatPrice(data?.totalNetSales) },
  { title: 'Working Days', value: data?.workingDays },
  { title: 'Profit', value: formatPrice(data?.profit) },
  {
    title: 'Profit %',
    value: data?.profitPercentage && `${data?.profitPercentage}%`,
  },
  {
    title: 'PRP',
    value:
      data?.productRevenuePercentage && `${data?.productRevenuePercentage}%`,
  },
];

export const revenueCellsTablet = [
  { row: '1/2', col: '1/3' },
  { row: '1/2', col: '3/4' },
  { row: '2/3', col: '1/2' },
  { row: '2/3', col: '2/3' },
  { row: '2/3', col: '3/4' },
];

export const employeePaymentsRoute = joinRoutes([
  Employees.Home,
  Employees.Details,
  Profile.Payments,
]);
